<template>
  <component
    :is="tag"
    v-bind="componentProps"
    :disabled="isButtonTag ? isDisabled : undefined"
    class="inline-flex items-center justify-center gap-2 font-semibold leading-none whitespace-nowrap no-underline border border-transparent cursor-pointer select-none transition-all duration-300 ease-out focus-visible:outline-2 focus-visible:outline-brand group"
    :class="[
      variantClasses,
      sizeClasses,
      roundedClasses,
      {
        'w-full': fullWidth,
        'opacity-40 cursor-not-allowed': isDisabled,
        'backdrop-blur-glass': ['glass', 'secondary'].includes(variant),
      },
    ]"
    @click="handleClick"
  >
    <span v-if="loading" class="animate-spin"
      ><Loader2 :size="iconSize"
    /></span>

    <span
      v-else-if="leadingIcon"
      class="transition-transform duration-300 group-hover:-translate-x-0.5"
    >
      <component :is="leadingIcon" :size="iconSize" :stroke-width="2.2" />
    </span>

    <span v-if="!iconOnly"
      ><slot>{{ label }}</slot></span
    >

    <span
      v-if="trailingIcon && !loading"
      class="transition-transform duration-300 group-hover:translate-x-0.5"
    >
      <component :is="trailingIcon" :size="iconSize" :stroke-width="2.2" />
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { Component, PropType } from "vue";
import { Loader2 } from "lucide-vue-next";

const NuxtLink = resolveComponent("NuxtLink");
const props = defineProps({
  label: { type: String, default: "" },
  to: { type: [String, Object], default: null },
  variant: {
    type: String as PropType<
      "primary" | "secondary" | "outline" | "ghost" | "glass" | "danger"
    >,
    default: "primary",
  },
  size: { type: String as PropType<"xs" | "sm" | "md" | "lg">, default: "md" },
  rounded: { type: String, default: "full" },
  fullWidth: Boolean,
  iconOnly: Boolean,
  loading: Boolean,
  disabled: Boolean,
  leadingIcon: [Object, Function] as PropType<Component>,
  trailingIcon: [Object, Function] as PropType<Component>,
});

const emit = defineEmits(["click"]);
const isDisabled = computed(() => props.disabled || props.loading);
const tag = computed(() => (props.to ? NuxtLink : "button"));
const isButtonTag = computed(() => tag.value === "button");

const iconSize = computed(
  () => ({ xs: 12, sm: 16, md: 18, lg: 20 })[props.size] || 18,
);

const variantClasses = computed(
  () =>
    ({
      primary:
        "bg-brand text-white shadow-[0_0_20px_-5px_rgba(255,95,0,0.4)] hover:-translate-y-0.5 active:scale-95",
      glass:
        "bg-white/3 text-white/80 border border-white/10 ring-1 ring-inset ring-white/5 hover:bg-white/8 hover:text-white hover:border-white/20 hover:-translate-y-0.5 shadow-glass",
      secondary:
        "bg-white/10 text-white border border-white/20 hover:bg-white/20 hover:-translate-y-0.5 shadow-lg",
      ghost: "text-white/60 hover:bg-white/10 hover:text-white",
      outline: "border-brand/40 text-brand hover:bg-brand/10",
      danger:
        "bg-red-500 text-white hover:bg-red-600 hover:-translate-y-0.5 shadow-lg",
    })[props.variant],
);

const sizeClasses = computed(
  () =>
    ({
      xs: "h-7 px-3 text-[11px]  tracking-wider",
      sm: "h-9 px-4 text-sm",
      md: "h-11 px-6 text-sm",
      lg: "h-13 px-8 text-base",
    })[props.size],
);

const roundedClasses = computed(
  () =>
    ({
      none: "rounded-none",
      sm: "rounded-md",
      md: "rounded-lg",
      full: "rounded-full",
    })[props.rounded],
);

const componentProps = computed(() =>
  props.to ? { to: props.to } : { type: "button" },
);

const handleClick = (e: Event) => !isDisabled.value && emit("click", e);
</script>
